import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, of, map, switchMap, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import {
  User,
  UserRole,
  LoginDto,
  LoginResponse,
  SwitchRoleResponse,
  ForgotPasswordDto,
  ResetPasswordDto,
  ChangePasswordDto,
} from '../models/user.model';

const SAVED_ROLE_KEY = 'adaptai_saved_role';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private apiUrl = `${environment.URL_SERVER}/api/v1/auth`;

  private _accessToken = signal<string | null>(null);
  private _currentUser = signal<User | null>(null);
  private _loading = signal(false);
  private _sessionChecked = signal(false);

  readonly currentUser = this._currentUser.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly isAuthenticated = computed(() => !!this._accessToken());
  readonly sessionChecked = this._sessionChecked.asReadonly();
  readonly userRole = computed(() => this._currentUser()?.role ?? null);
  readonly userRoles = computed(() => {
    const u = this._currentUser();
    if (!u) return [];
    return u.roles ?? [u.role];
  });
  readonly hasMultipleRoles = computed(() => this.userRoles().length > 1);

  getToken(): string | null {
    return this._accessToken();
  }

  hasRole(...roles: UserRole[]): boolean {
    const role = this._currentUser()?.role;
    return role ? roles.includes(role) : false;
  }

  login(credentials: LoginDto): Observable<ApiResponse<LoginResponse>> {
    this._loading.set(true);
    return this.http
      .post<ApiResponse<LoginResponse>>(`${this.apiUrl}/login`, credentials, {
        withCredentials: true,
      })
      .pipe(
        tap((res) => {
          if (res.success) {
            this._accessToken.set(res.data.accessToken);
            const u = res.data.user as any;
            this._currentUser.set({
              id: u.id,
              email: u.email ?? null,
              first_name: u.firstName ?? u.first_name,
              last_name: u.lastName ?? u.last_name,
              role: u.role,
              roles: u.roles ?? [u.role],
              is_active: u.isActive ?? u.is_active ?? true,
              institution_id: u.institutionId ?? u.institution_id ?? null,
              document_type: u.documentType ?? u.document_type ?? null,
              document_number: u.documentNumber ?? u.document_number ?? null,
              created_at: u.createdAt ?? u.created_at ?? '',
            });
            this._sessionChecked.set(true);
          }
          this._loading.set(false);
        }),
        catchError((err) => {
          this._loading.set(false);
          return throwError(() => err);
        }),
      );
  }

  loadProfile(): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/me`, { withCredentials: true }).pipe(
      tap((res) => {
        if (res.success) {
          this._currentUser.set(res.data);
        }
      }),
    );
  }

  refreshToken(): Observable<ApiResponse<{ accessToken: string }>> {
    return this.http
      .post<ApiResponse<{ accessToken: string }>>(`${this.apiUrl}/refresh`, {}, { withCredentials: true })
      .pipe(
        tap((res) => {
          if (res.success) {
            this._accessToken.set(res.data.accessToken);
          }
        }),
        catchError((err) => {
          this.clearSession(false);
          return throwError(() => err);
        }),
      );
  }

  logout(): void {
    this.http.post(`${this.apiUrl}/logout`, {}, { withCredentials: true }).subscribe({
      complete: () => this.clearSession(true),
      error: () => this.clearSession(true),
    });
  }

  forgotPassword(dto: ForgotPasswordDto): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/forgot-password`, dto);
  }

  resetPassword(dto: ResetPasswordDto): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/reset-password`, dto);
  }

  changePassword(dto: ChangePasswordDto): Observable<ApiResponse<{ message: string }>> {
    return this.http.patch<ApiResponse<{ message: string }>>(`${this.apiUrl}/change-password`, dto);
  }

  updateProfile(data: Partial<User>): Observable<ApiResponse<User>> {
    return this.http.patch<ApiResponse<User>>(`${this.apiUrl}/me`, data).pipe(
      tap((res) => {
        if (res.success) {
          this._currentUser.set(res.data);
        }
      }),
    );
  }

  uploadAvatar(file: File): Observable<ApiResponse<{ avatarUrl: string }>> {
    const formData = new FormData();
    formData.append('avatar', file);
    return this.http.post<ApiResponse<{ avatarUrl: string }>>(`${this.apiUrl}/me/avatar`, formData).pipe(
      tap((res) => {
        if (res.success) {
          const user = this._currentUser();
          if (user) {
            this._currentUser.set({ ...user, avatar_url: res.data.avatarUrl });
          }
        }
      }),
    );
  }

  async tryRestoreSession(): Promise<boolean> {
    if (this._sessionChecked()) {
      return this.isAuthenticated();
    }

    try {
      const refreshRes = await firstValueFrom(
        this.http.post<ApiResponse<{ accessToken: string }>>(
          `${this.apiUrl}/refresh`, {}, { withCredentials: true },
        ),
      );

      if (refreshRes.success) {
        this._accessToken.set(refreshRes.data.accessToken);

        const profileRes = await firstValueFrom(this.loadProfile());
        this._sessionChecked.set(true);
        return this.isAuthenticated();
      }
    } catch {
      // No valid refresh token — user needs to log in
    }

    this._sessionChecked.set(true);
    return false;
  }

  getRedirectRouteForRole(role: UserRole): string {
    const routes: Record<UserRole, string> = {
      [UserRole.SUPER_ADMIN]: '/super-admin',
      [UserRole.ADMIN]: '/admin',
      [UserRole.TEACHER]: '/teacher',
      [UserRole.STUDENT]: '/student',
      [UserRole.PARENT]: '/parent',
    };
    return routes[role] || '/login';
  }

  switchRole(role: UserRole): Observable<ApiResponse<SwitchRoleResponse>> {
    return this.http
      .post<ApiResponse<SwitchRoleResponse>>(`${this.apiUrl}/switch-role`, { role }, { withCredentials: true })
      .pipe(
        tap((res) => {
          if (res.success) {
            const u = res.data.user as any;
            this._currentUser.set({
              id: u.id,
              email: u.email ?? null,
              first_name: u.firstName ?? u.first_name,
              last_name: u.lastName ?? u.last_name,
              role: res.data.role,
              roles: res.data.roles,
              is_active: u.isActive ?? u.is_active ?? true,
              institution_id: u.institutionId ?? u.institution_id ?? null,
              document_type: u.documentType ?? u.document_type ?? null,
              document_number: u.documentNumber ?? u.document_number ?? null,
              created_at: u.createdAt ?? u.created_at ?? '',
            });
          }
        }),
      );
  }

  getSavedRole(): UserRole | null {
    return (localStorage.getItem(SAVED_ROLE_KEY) as UserRole) ?? null;
  }

  setSavedRole(role: UserRole): void {
    localStorage.setItem(SAVED_ROLE_KEY, role);
  }

  clearSavedRole(): void {
    localStorage.removeItem(SAVED_ROLE_KEY);
  }

  private clearSession(navigate: boolean): void {
    this._accessToken.set(null);
    this._currentUser.set(null);
    this.clearSavedRole();
    if (navigate) {
      this.router.navigate(['/login']);
    }
  }
}
